import type { components } from "@/api/openapi";
import { type Page, typedApi } from "@/api/typed-client";
import { useQuery } from "@tanstack/react-query";

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "detail" in error) {
    // biome-ignore lint/suspicious/noExplicitAny: API error shape is untyped
    const detail = (error as any).detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return JSON.stringify(detail);
  }
  return "Unknown error";
}

// Re-export schema types for convenience
export type RecipeOut = components["schemas"]["RecipeOut"];
export type RecipeDetail = components["schemas"]["RecipeDetail"];
export type RecipeSearchResult = components["schemas"]["RecipeSearchResult"];
export type RecipeVersionOut = components["schemas"]["RecipeVersionOut"];
export type RecipeGraphResponse = components["schemas"]["RecipeGraphResponse"];
export type RecipeGraphNode = components["schemas"]["RecipeGraphNode"];
export type RecipeGraphEdge = components["schemas"]["RecipeGraphEdge"];
export type RecipeAttachmentOut = components["schemas"]["RecipeAttachmentOut"];
export type RecipeRelationOut = components["schemas"]["RecipeRelationOut"];

interface UseRecipesOptions {
  search?: string;
  family?: "tabular" | "anomaly" | "forecast";
  tag?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Fetch recipes list or search results.
 * If search is provided, hits /v1/recipes/search, else /v1/recipes.
 */
export function useRecipes(projectId: string | null, opts?: UseRecipesOptions) {
  const isSearch = !!opts?.search;
  const page = opts?.page ?? 1;
  const pageSize = opts?.pageSize ?? 20;

  return useQuery({
    enabled: !!projectId,
    queryKey: isSearch
      ? ["recipes", "search", projectId, opts?.search, opts?.family, opts?.tag]
      : ["recipes", projectId, opts?.family, opts?.tag, page, pageSize],
    queryFn: async () => {
      if (isSearch && opts?.search && projectId) {
        // Search endpoint — pagination intentionally omitted; limit fixed at 100
        const { data, error } = await typedApi.GET("/v1/recipes/search", {
          params: {
            query: {
              query: opts.search,
              project_id: projectId,
              family: opts.family ?? null,
              tags: opts.tag ? [opts.tag] : null,
              limit: 100,
            },
          },
        });

        if (error) throw new Error(getErrorMessage(error) || "Search failed");
        return data as Page<RecipeSearchResult>;
      }

      // List endpoint
      const { data, error } = await typedApi.GET("/v1/recipes", {
        params: {
          query: {
            // biome-ignore lint/style/noNonNullAssertion: guarded by enabled: !!projectId
            project_id: projectId!,
            family: opts?.family ?? null,
            tags: opts?.tag ? [opts.tag] : null,
            include_drafts: true,
            // Justified: recipes list schema lacks page/page_size params; backend accepts them
            page,
            page_size: pageSize,
          } as {
            project_id: string;
            family?: "tabular" | "anomaly" | "forecast" | null;
            tags?: string[] | null;
            include_drafts?: boolean;
          },
        },
      });

      if (error) throw new Error(getErrorMessage(error) || "Failed to fetch recipes");
      return data as Page<RecipeOut>;
    },
    placeholderData: isSearch ? undefined : (prev) => prev,
  });
}

/**
 * Fetch a single recipe by ID.
 * Optionally specify version (number, "live", or "committed").
 */
export function useRecipe(id: string | null, version?: number | "live" | "committed") {
  return useQuery({
    enabled: !!id,
    queryKey: ["recipe", id, version],
    queryFn: async () => {
      const params: {
        path: { recipe_id: string };
        query?: { version?: string | null };
      } = {
        // biome-ignore lint/style/noNonNullAssertion: guarded by enabled: !!id
        path: { recipe_id: id! },
      };
      if (version) {
        params.query = { version: String(version) };
      }

      const { data, error } = await typedApi.GET("/v1/recipes/{recipe_id}", {
        params,
      });

      if (error) throw new Error(getErrorMessage(error) || "Failed to fetch recipe");
      return data as RecipeDetail;
    },
  });
}

/**
 * Fetch version history for a recipe.
 */
export function useRecipeVersions(id: string | null) {
  return useQuery({
    enabled: !!id,
    queryKey: ["recipe", id, "versions"],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/recipes/{recipe_id}/versions", {
        params: {
          // biome-ignore lint/style/noNonNullAssertion: guarded by enabled: !!id
          path: { recipe_id: id! },
        },
      });

      if (error) throw new Error(getErrorMessage(error) || "Failed to fetch versions");
      return data as Page<RecipeVersionOut>;
    },
  });
}

/**
 * Fetch the relation graph for a recipe.
 */
export function useRecipeGraph(
  id: string | null,
  depth?: number,
  direction?: "both" | "prerequisites" | "followups",
) {
  return useQuery({
    enabled: !!id,
    queryKey: ["recipe", id, "graph", depth, direction],
    queryFn: async () => {
      const params: {
        path: { recipe_id: string };
        query?: { depth?: number; direction?: "both" | "prerequisites" | "followups" };
      } = {
        // biome-ignore lint/style/noNonNullAssertion: guarded by enabled: !!id
        path: { recipe_id: id! },
      };
      if (depth !== undefined || direction !== undefined) {
        params.query = { depth, direction };
      }

      const { data, error } = await typedApi.GET("/v1/recipes/{recipe_id}/graph", {
        params,
      });

      if (error) throw new Error(getErrorMessage(error) || "Failed to fetch graph");
      return data as RecipeGraphResponse;
    },
  });
}
